import { InflateRequestMode } from '@sccs/types';
import { RequestHeaderConfiguration } from '../../../types/ApiClientConfig';
import { SharedRequestConfiguration, SharedRequestDlsParameter } from '../../../types/GeneralParameter';
import { SearchAlternativeReponseWithId, SearchResponseDTO } from '../../../types/dls/dls-response/SearchResponseDto';
import { convertFieldsToStrings } from '../../../util/ConvertPropertiesToString';
import { splitModelIdentifier } from '../../../util/SplitData';
import { isCommercialVanBy } from '../../../util/VansConfiguration';
import { createFilterCriteriaString, getVansFilterCriteria } from '../../../util/dls/HandleFilterCriteria';
import { getRequestConfiguration } from '../../../util/dls/RequestConfiguration';
import { GenericFetchClient, ModeSpecificFetchClient } from '../../GenericFetchClient';

/**
 * Available query parameters at dls endpoint /search only
 */
export type DlsSearch = SharedRequestDlsParameter & {
  includeFirstVehicles: number;
  sort?: string;
  filterCriteria: FilterCriteriaDlsSearch;
};

export type FilterCriteriaDlsSearch = {
  modelIdentifier: string[];
  brand: string[];
  bodyType: string[];
  fuelType: string[] | undefined;
  campaign: string | undefined;
};

export class DlsSearchClient extends GenericFetchClient implements ModeSpecificFetchClient {
  constructor(config: RequestHeaderConfiguration) {
    super(config);
    this.setPathSegment('/search');
  }
  getInflateIdLength(): number {
    throw new Error('Method not implemented.');
  }
  setMode(): void {
    throw new Error('Method not implemented.');
  }

  setRequiredParameters({ filterCriteria, ...rest }: DlsSearch, config: SharedRequestConfiguration): void {
    const additionalRequestConfig = this.getAdditionalRequestConfig({ filterCriteria, ...rest }, config);

    this.setQueryParameters({
      ...convertFieldsToStrings(rest),
      ctx: additionalRequestConfig.ctx,
      vehicleAssortment: additionalRequestConfig.vehicleAssortment,
      filterCriteria: additionalRequestConfig.filters,
    });
  }

  private getAdditionalRequestConfig({ filterCriteria, ...rest }: DlsSearch, config: SharedRequestConfiguration) {
    let isVan = false;
    filterCriteria.modelIdentifier.forEach((modelId) => {
      isVan = isCommercialVanBy(modelId);
    });

    const { vehicleAssortment, ctx } = getRequestConfiguration(isVan, {
      ...rest,
      filterCriteria,
    });

    return {
      ctx: ctx ?? '',
      vehicleAssortment: rest.vehicleAssortment ?? vehicleAssortment ?? '',
      filters: this.getUpdatedFilterCriteria(isVan, filterCriteria, config),
    };
  }

  private getUpdatedFilterCriteria(
    isVan: boolean,
    filter: FilterCriteriaDlsSearch,
    config: SharedRequestConfiguration,
  ): string {
    if (isVan) {
      return getVansFilterCriteria(filter);
    }
    if (config.mode === InflateRequestMode.SIMPLE_PRICE) {
      const splittedModelIdentifer = splitModelIdentifier(filter.modelIdentifier[0]);
      const modelIdentifier = !filter.modelIdentifier ? '' : splittedModelIdentifer.model;

      const bodyType = !filter.modelIdentifier
        ? Array.isArray(filter.bodyType)
          ? filter.bodyType.join(',')
          : (filter.bodyType ?? '')
        : splittedModelIdentifer.body;

      const filterCriteria = {
        modelIdentifier,
        bodyType,
        brand: filter.brand.join(','),
        fuelType: filter.fuelType?.join(','),
        campaign: filter.campaign,
      };

      return createFilterCriteriaString(filterCriteria);
    } else {
      const filterCriteria = {
        modelIdentifier: filter.modelIdentifier.join(','),
        bodyType: filter.bodyType?.join(','),
        brand: filter.brand.join(','),
        fuelType: filter.fuelType?.join(','),
        campaign: filter.campaign,
      };

      return createFilterCriteriaString(filterCriteria);
    }
  }

  checkAllRequiredParametersPresent(): boolean {
    throw new Error('Method not implemented.');
  }

  async getResponse(): Promise<SearchAlternativeReponseWithId> {
    const dlsSearchData = await this.fetchTypedBody<SearchResponseDTO>();
    return {
      responseTypeId: 'SearchResponseDTO',
      ...dlsSearchData,
    };
  }
}
